import { useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { copyText } from "../util/clipboard";

/**
 * Renders GitHub-flavored markdown with syntax-highlighted code blocks.
 * react-markdown does not emit raw HTML and rehype-highlight operates on the
 * AST, so user-authored content (descriptions, comments) is XSS-safe. Links
 * open in a new tab with safe rel attributes; code blocks get a copy button.
 */
export function Markdown({ children }: { children: string }) {
  return (
    <div className="prose-kandoo text-slate-700">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeHighlight, { detect: true, ignoreMissing: true }]]}
        components={{
          a: ({ node: _node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer nofollow" />
          ),
          pre: ({ node: _node, children }) => <CodeBlock>{children}</CodeBlock>,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

function CodeBlock({ children }: { children: React.ReactNode }) {
  const preRef = useRef<HTMLPreElement | null>(null);
  const [copied, setCopied] = useState(false);

  async function copy(e: React.MouseEvent) {
    // don't let the click reach a click-to-edit wrapper (description field)
    e.stopPropagation();
    const ok = await copyText(preRef.current?.innerText ?? "");
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={copy}
        title={copied ? "Copied" : "Copy code"}
        aria-label={copied ? "Copied" : "Copy code"}
        className="absolute right-2 top-2 rounded bg-slate-200 p-1 text-slate-600 opacity-0 transition hover:bg-slate-300 focus:opacity-100 group-hover:opacity-100"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
      <pre ref={preRef}>{children}</pre>
    </div>
  );
}
